import React, { memo } from 'react';
import {
  isParentElement,
  isBuildingVersionElement,
  getChildElements,
} from '@shared/helpers/recursive-element.helpers';
import { OneOfListElements } from '@shared/models/project.model';
import NestedElementList, { INestedListBase } from './NestedElementList';
import { useIsElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import NestedElementItem from './NestedElementItem';

export interface INestedElementProps<
  T extends OneOfListElements,
> extends INestedListBase {
  element: T;
}

const NestedElement = <T extends OneOfListElements>({
  element,
  indentation = 0,
  selectMode,
  useFilterSortChildren = getChildElements,
  cullBeforeMount,
  rowHeight,
}: INestedElementProps<T>) => {
  const isBuildingVersion = isBuildingVersionElement(element);
  const sortedChildren = useFilterSortChildren(element);
  const expanded = useIsElementExpanded(element);

  const showChildren =
    isParentElement(element) &&
    !!sortedChildren.length &&
    !isBuildingVersion &&
    !selectMode;

  return (
    <>
      {/* Element Row */}
      <NestedElementItem
        key={`nested-element-${element.id}`}
        element={element}
        indentation={indentation}
        selectMode={selectMode}
        cullBeforeMount={cullBeforeMount}
        rowHeight={rowHeight}
      />

      {/* Children of element */}
      {showChildren && (
        <NestedElementList
          key={`nested-list-${element.id}`}
          elements={sortedChildren}
          expanded={expanded}
          indentation={indentation + 1}
          useFilterSortChildren={useFilterSortChildren}
          cullBeforeMount={cullBeforeMount}
          rowHeight={rowHeight}
        />
      )}
    </>
  );
};

export default memo(NestedElement);
